import React, { useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { Grid, TextField, MenuItem, Checkbox, ListItemText, InputAdornment } from '@material-ui/core';
import { InvertedChevronIcon } from 'components/Icons';

import { localeDateToISO, inputDateTimeMask, isAValidDate } from 'utils/DateUtils';
import textRange from 'utils/TextRange';

import { filtrosReducer, filtrosInitialState } from './reducer';
import useStyle from './style';

interface FilterParams {
  coops?: string[];
  colaborador?: boolean;
  data?: string;
  cpfOrNome?: string;
}

interface Props {
  listaCoops: Array<string>;
  onChangeFilter: (params: FilterParams) => void;
}

const DATETIME_PATTERN = /\d{2}\/\d{2}\/\d{4}\s\d{2}:\d{2}:\d{2}/;

export default function ({ listaCoops, onChangeFilter }: Props) {
  const { t } = useTranslation();
  const classes = useStyle();
  const [{ cpfOrNome, coops, data, colaborador, dataHasError }, dispatch] = useReducer(
    filtrosReducer,
    filtrosInitialState
  );

  const handleCoopsChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const value = event.target.value as string[];
      dispatch({ type: 'setCoops', value });
      onChangeFilter({ coops: value.length > 0 ? value : undefined });
    },
    [onChangeFilter]
  );

  const handleColaboradorChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const numericValue = event.target.value as number;
      if (numericValue < 0) {
        dispatch({ type: 'setColaborador', value: undefined });
        onChangeFilter({ colaborador: undefined });
      } else {
        dispatch({ type: 'setColaborador', value: numericValue > 0 });
        onChangeFilter({ colaborador: numericValue > 0 });
      }
    },
    [onChangeFilter]
  );

  const handleDataChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.replace(/[^0-9:/\s]/g, '');
      if (data.length === 19 && data.length > value.length) {
        onChangeFilter({ data: undefined });
      }
      dispatch({ type: 'setData', value });
      !DATETIME_PATTERN.test(value) && dispatch({
        type: 'setDataError',
        value: true,
      });
      if (DATETIME_PATTERN.test(value)) {
        const dateSplit = value.split(/\s/g);
        const isoDate = localeDateToISO(dateSplit[0]);
        const isoDateTime = `${isoDate}T${dateSplit[1]}.000`;
        if (isAValidDate(isoDateTime)) {
          dispatch({
            type: 'setDataError',
            value: false,
          });
          onChangeFilter({ data: isoDateTime });
        } else {
          dispatch({
            type: 'setDataError',
            value: true,
          });
        }
      }
    },
    [data, onChangeFilter]
  );

  const handleDataBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (event.target.value.length === 0) {
      dispatch({
        type: 'setDataError',
        value: false,
      })
    }
  };

  const handleCPFChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = textRange(event.target.value, 35).replace(/[-/]/g, '').replace(/\.(?=\d)/g, '');
      dispatch({ type: 'setSearch', value });
      if (value.length >= 3) {
        onChangeFilter({ cpfOrNome: value });
      } else {
        onChangeFilter({ cpfOrNome: undefined });
      }
    },
    [onChangeFilter]
  );

  return (
    <Grid container spacing={3} className={classes.root}>
      <Grid item xs={12} sm={12} md={3}>
        <TextField
          select
          label={t('RelatorioPresentes.labelFiltroCoops')}
          SelectProps={{
            IconComponent: () => (
              <InputAdornment position="end" className={classes.selectIcon}>
                <InvertedChevronIcon />
              </InputAdornment>
            ),
            multiple: true,
            value: coops,
            renderValue: selected => (selected as string[]).join(', '),
            onChange: handleCoopsChange,
          }}
          disabled={!listaCoops.length}
          fullWidth
        >
          {listaCoops.map(entry => (
            <MenuItem key={entry} value={entry}>
              <Checkbox checked={(coops || []).includes(entry)} color="primary" />
              <ListItemText primary={entry} />
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={12} md={3}>
        <TextField
          select
          label={t('RelatorioPresentes.labelFiltroColaborador')}
          SelectProps={{
            IconComponent: () => (
              <InputAdornment position="end" className={classes.selectIcon}>
                <InvertedChevronIcon />
              </InputAdornment>
            ),
            value: colaborador !== undefined ? (colaborador ? 1 : 0) : -1,
            renderValue: selected => {
              if ((selected as number) === 1) return 'Sim';
              if ((selected as number) === 0) return 'Não';
              return 'Todos';
            },
            onChange: handleColaboradorChange,
          }}
          fullWidth
        >
          <MenuItem value={-1} selected={colaborador === undefined}>
            Todos
          </MenuItem>
          <MenuItem value={1} selected={colaborador}>
            Sim
          </MenuItem>
          <MenuItem value={0} selected={colaborador === false}>
            Não
          </MenuItem>
        </TextField>
      </Grid>
      <Grid item xs={12} sm={12} md={3}>
        <TextField
          label={t('RelatorioPresentes.labelFiltroData')}
          placeholder="dd/mm/aaaa hh:mm:ss"
          value={inputDateTimeMask(data)}
          onChange={handleDataChange}
          onBlur={handleDataBlur}
          inputProps={{
            maxLength: 19,
          }}
          error={dataHasError}
          helperText={t('RelatorioPresentes.helperFiltroData')}
          fullWidth
        />
      </Grid>
      <Grid item xs={12} sm={12} md={3}>
        <TextField
          label={t('RelatorioPresentes.labelFiltroCPFOrNpme')}
          value={cpfOrNome}
          onChange={handleCPFChange}
          fullWidth
        />
      </Grid>
    </Grid>
  );
}
